import type { ExerciseDef } from './types'

// ─── 6.3 Many features ────────────────────────────────────────────────────────

const STANDARDIZE = `// (from the tensors lesson) centre each column and divide by its spread
function standardize(X: number[][]): { Z: number[][]; mean: number[]; std: number[] } {
  const d = X[0].length
  const mean = range(d).map(j => vec.mean(X.map(x => x[j])))
  const std = range(d).map(j => Math.sqrt(vec.mean(X.map(x => (x[j] - mean[j]) ** 2))))
  return { Z: X.map(x => x.map((v, j) => (v - mean[j]) / std[j])), mean, std }
}`

export const rawFeatures: ExerciseDef = {
  starter: `const { X, y, features } = data.housing(200)
console.log(features)
console.table(X.slice(0, 5).map((x, i) => ({ size: x[0], bedrooms: x[1], age: x[2], km: x[3], price: y[i] })))

// Gradient descent on the RAW numbers: sizes are ~100s, bedroom counts ~1–6.
// The size weight's gradient is huge, the others tiny — one learning rate can't suit them all.
for (const lr of [1e-6, 1e-5, 1e-4]) {
  let w = [0, 0, 0, 0], b = 0
  for (let epoch = 0; epoch < 200; epoch++) {
    const gw = [0, 0, 0, 0]
    let gb = 0
    for (let i = 0; i < X.length; i++) {
      const err = vec.dot(w, X[i]) + b - y[i]
      for (let j = 0; j < 4; j++) gw[j] += (2 * err * X[i][j]) / X.length
      gb += (2 * err) / X.length
    }
    w = w.map((v, j) => v - lr * gw[j])
    b -= lr * gb
  }
  const mse = vec.mean(X.map((x, i) => (vec.dot(w, x) + b - y[i]) ** 2))
  console.log("lr", lr, "→ error", round(mse, 1))
}
`,
}

export const multiFit: ExerciseDef = {
  starter: `const { X, y } = data.housing(200)

${STANDARDIZE}

// 1. predict(w, b, x): one weight per feature — the dot product plus the bias.
function predict(w: number[], b: number, x: number[]): number {
  return 0
}

// 2. train(Z, y, lr, epochs): gradient descent on mean squared error.
//    For every weight j:  ∂L/∂wⱼ = (2/n) Σ (ŷᵢ − yᵢ)·zᵢⱼ      and   ∂L/∂b = (2/n) Σ (ŷᵢ − yᵢ)
function train(Z: number[][], y: number[], lr: number, epochs: number): { w: number[]; b: number } {
  let w = new Array(Z[0].length).fill(0)
  let b = 0
  return { w, b }
}

const { Z, std } = standardize(X)
const model = train(Z, y, 0.1, 500)
// Each standardised weight is "thousands per standard deviation". Divide by the spread to get "per unit":
const perUnit = model.w.map((w, j) => w / std[j])
console.log("price per extra m²:", round(perUnit[0], 2), "  per bedroom:", round(perUnit[1], 1), "  per year of age:", round(perUnit[2], 2), "  per km:", round(perUnit[3], 2))
`,
  solution: `const { X, y } = data.housing(200)

${STANDARDIZE}

function predict(w: number[], b: number, x: number[]): number {
  return vec.dot(w, x) + b
}

function train(Z: number[][], y: number[], lr: number, epochs: number): { w: number[]; b: number } {
  let w = new Array(Z[0].length).fill(0)
  let b = 0
  const n = Z.length
  for (let e = 0; e < epochs; e++) {
    const gw = new Array(w.length).fill(0)
    let gb = 0
    for (let i = 0; i < n; i++) {
      const err = predict(w, b, Z[i]) - y[i]
      for (let j = 0; j < w.length; j++) gw[j] += (2 * err * Z[i][j]) / n
      gb += (2 * err) / n
    }
    w = w.map((v, j) => v - lr * gw[j])
    b -= lr * gb
  }
  return { w, b }
}

const { Z, std } = standardize(X)
const model = train(Z, y, 0.1, 500)
const perUnit = model.w.map((w, j) => w / std[j])
console.log("price per extra m²:", round(perUnit[0], 2), "  per bedroom:", round(perUnit[1], 1), "  per year of age:", round(perUnit[2], 2), "  per km:", round(perUnit[3], 2))
`,
  tests: `
test("predict([2, 3], 1, [10, 100]) is 321", () => expect(predict([2, 3], 1, [10, 100]), "predict").toBe(321))
test("training finds a near-perfect fit on y = 3a − 2b + 5", () => {
  const __Z = [[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, -1]]
  const __m = train(__Z, __Z.map(z => 3 * z[0] - 2 * z[1] + 5), 0.1, 400)
  expect([__m.w[0], __m.w[1], __m.b], "[w0, w1, b]").toBeCloseTo([3, -2, 5], 1e-3)
})
test("each extra m² adds about 2 (thousand)", () => expect(perUnit[0], "per m²").toBeBetween(1.8, 2.2))
test("each year of age takes off about 0.8", () => expect(perUnit[2], "per year").toBeBetween(-1, -0.6))
`,
}

// ─── 6.5 Many classes ─────────────────────────────────────────────────────────

const SOFTMAX_HELPERS = `function oneHot(label: number, k: number): number[] {
  const v = new Array(k).fill(0)
  v[label] = 1
  return v
}

// W has one row per input feature and one column per class.
function scores(W: number[][], b: number[], x: number[]): number[] {
  return b.map((bk, k) => {
    let s = bk
    for (let j = 0; j < x.length; j++) s += x[j] * W[j][k]
    return s
  })
}`

export const softmaxRegression: ExerciseDef = {
  starter: `// 1. oneHot(label, k): a list of k zeros with a 1 at position label.  oneHot(2, 4) → [0, 0, 1, 0]
function oneHot(label: number, k: number): number[] {
  return []
}

// 2. scores(W, b, x): one score per class:  score_k = b_k + Σⱼ xⱼ·W[j][k]
//    (W has one ROW per input feature and one COLUMN per class.)
function scores(W: number[][], b: number[], x: number[]): number[] {
  return b
}

// 3. train(X, y, k, lr, epochs): softmax regression. For every example:
//      p = vec.softmax(scores(W, b, x)),   e = p − oneHot(y)        (the same lovely gradient as before!)
//      ∂L/∂W[j][c] += e[c]·x[j] / n,       ∂L/∂b[c] += e[c] / n
function train(X: number[][], y: number[], k: number, lr: number, epochs: number) {
  const W = X[0].map(() => new Array(k).fill(0))
  const b = new Array(k).fill(0)
  return { W, b }
}

function accuracy(model: { W: number[][]; b: number[] }, X: number[][], y: number[]): number {
  return X.filter((x, i) => vec.argmax(scores(model.W, model.b, x)) === y[i]).length / X.length
}

const d = data.blobs(300, 3, 0.5)
const model = train(d.X, d.y, 3, 0.5, 200)
console.log("accuracy:", accuracy(model, d.X, d.y))
plot.scatter(d.X, { labels: d.y, title: "three classes" })
`,
  solution: `${SOFTMAX_HELPERS}

function train(X: number[][], y: number[], k: number, lr: number, epochs: number) {
  const W = X[0].map(() => new Array(k).fill(0))
  const b = new Array(k).fill(0)
  const n = X.length
  for (let e = 0; e < epochs; e++) {
    const gW = X[0].map(() => new Array(k).fill(0))
    const gb = new Array(k).fill(0)
    for (let i = 0; i < n; i++) {
      const p = vec.softmax(scores(W, b, X[i]))
      const t = oneHot(y[i], k)
      for (let c = 0; c < k; c++) {
        const err = (p[c] - t[c]) / n
        gb[c] += err
        for (let j = 0; j < X[i].length; j++) gW[j][c] += err * X[i][j]
      }
    }
    for (let j = 0; j < W.length; j++) for (let c = 0; c < k; c++) W[j][c] -= lr * gW[j][c]
    for (let c = 0; c < k; c++) b[c] -= lr * gb[c]
  }
  return { W, b }
}

function accuracy(model: { W: number[][]; b: number[] }, X: number[][], y: number[]): number {
  return X.filter((x, i) => vec.argmax(scores(model.W, model.b, x)) === y[i]).length / X.length
}

const d = data.blobs(300, 3, 0.5)
const model = train(d.X, d.y, 3, 0.5, 200)
console.log("accuracy:", accuracy(model, d.X, d.y))
plot.scatter(d.X, { labels: d.y, title: "three classes" })
`,
  tests: `
test("oneHot(2, 4) is [0, 0, 1, 0]", () => expect(oneHot(2, 4), "oneHot(2, 4)").toEqual([0, 0, 1, 0]))
test("scores adds the bias and the weighted inputs", () => expect(scores([[1, 0], [0, 2]], [10, 20], [3, 4]), "scores").toEqual([13, 28]))
test("three blobs: at least 95% right", () => expect(accuracy(model, d.X, d.y), "accuracy").toBeGreaterThan(0.95))
test("works for 4 classes too", () => {
  const __X = [[2, 0], [-2, 0], [0, 2], [0, -2], [2.2, 0.1], [-2.1, 0.2], [0.1, 2.3], [0.2, -2.2]]
  const __y = [0, 1, 2, 3, 0, 1, 2, 3]
  expect(accuracy(train(__X, __y, 4, 0.5, 300), __X, __y), "accuracy").toBe(1)
})
`,
}

export const digitsLinear: ExerciseDef = {
  starter: `${SOFTMAX_HELPERS}

// (your train from the last challenge)
function train(X: number[][], y: number[], k: number, lr: number, epochs: number) {
  const W = X[0].map(() => new Array(k).fill(0))
  const b = new Array(k).fill(0)
  const n = X.length
  for (let e = 0; e < epochs; e++) {
    const gW = X[0].map(() => new Array(k).fill(0))
    const gb = new Array(k).fill(0)
    for (let i = 0; i < n; i++) {
      const p = vec.softmax(scores(W, b, X[i]))
      for (let c = 0; c < k; c++) {
        const err = (p[c] - (y[i] === c ? 1 : 0)) / n
        gb[c] += err
        for (let j = 0; j < X[i].length; j++) gW[j][c] += err * X[i][j]
      }
    }
    for (let j = 0; j < W.length; j++) for (let c = 0; c < k; c++) W[j][c] -= lr * gW[j][c]
    for (let c = 0; c < k; c++) b[c] -= lr * gb[c]
  }
  return { W, b }
}

// 1000 handwritten digits, 14×14 pixels each = 196 inputs, 10 classes.
const { X, y } = data.digits(1000)
const trainX = X.slice(0, 800), trainY = y.slice(0, 800)
const testX = X.slice(800), testY = y.slice(800)

// Train on the first 800 and measure accuracy on the 200 the model never saw.
// (Takes a couple of seconds: 800 images × 196 pixels × 10 classes × 60 passes.)
const model = train(trainX, trainY, 10, 1, 60)
const testAccuracy = 0   // ← compute it: the fraction of testX whose top score matches testY

console.log("test accuracy:", testAccuracy)
// Each class's 196 weights, shown as a 14×14 picture: what the model "looks for".
plot.heatmap(range(14).map(r => range(14).map(c => model.W[r * 14 + c][3])), { title: "weights for the digit 3 (bright = evidence for 3)" })
`,
  solution: `${SOFTMAX_HELPERS}

function train(X: number[][], y: number[], k: number, lr: number, epochs: number) {
  const W = X[0].map(() => new Array(k).fill(0))
  const b = new Array(k).fill(0)
  const n = X.length
  for (let e = 0; e < epochs; e++) {
    const gW = X[0].map(() => new Array(k).fill(0))
    const gb = new Array(k).fill(0)
    for (let i = 0; i < n; i++) {
      const p = vec.softmax(scores(W, b, X[i]))
      for (let c = 0; c < k; c++) {
        const err = (p[c] - (y[i] === c ? 1 : 0)) / n
        gb[c] += err
        for (let j = 0; j < X[i].length; j++) gW[j][c] += err * X[i][j]
      }
    }
    for (let j = 0; j < W.length; j++) for (let c = 0; c < k; c++) W[j][c] -= lr * gW[j][c]
    for (let c = 0; c < k; c++) b[c] -= lr * gb[c]
  }
  return { W, b }
}

const { X, y } = data.digits(1000)
const trainX = X.slice(0, 800), trainY = y.slice(0, 800)
const testX = X.slice(800), testY = y.slice(800)

const model = train(trainX, trainY, 10, 1, 60)
const testAccuracy = testX.filter((x, i) => vec.argmax(scores(model.W, model.b, x)) === testY[i]).length / testX.length

console.log("test accuracy:", testAccuracy)
plot.heatmap(range(14).map(r => range(14).map(c => model.W[r * 14 + c][3])), { title: "weights for the digit 3 (bright = evidence for 3)" })
`,
  tests: `
test("a straight-line model reads over 80% of unseen digits", () => expect(testAccuracy, "testAccuracy").toBeGreaterThan(0.8))
test("…but not all of them (a linear model has limits)", () => expect(testAccuracy, "testAccuracy").toBeLessThan(0.99))
`,
}

// ─── 6.6 Evaluating models ────────────────────────────────────────────────────

export const accuracyParadox: ExerciseDef = {
  starter: `// 1 in 20 transactions is fraud. A "model" that ALWAYS says "not fraud":
const truth = range(1000).map(i => (i % 20 === 0 ? 1 : 0))
const lazy = truth.map(() => 0)

const accuracy = truth.filter((t, i) => t === lazy[i]).length / truth.length
console.log("accuracy:", accuracy)                                       // 95%!
console.log("frauds caught:", truth.filter((t, i) => t === 1 && lazy[i] === 1).length, "of", truth.filter(t => t === 1).length)
`,
}

export const metrics: ExerciseDef = {
  starter: `// 1. confusion(truth, pred): count the four kinds of outcome (labels are 1 = positive, 0 = negative).
function confusion(truth: number[], pred: number[]): { tp: number; fp: number; tn: number; fn: number } {
  return { tp: 0, fp: 0, tn: 0, fn: 0 }
}

// 2. scores(c):
//    precision = tp / (tp + fp)   — of everything flagged, how much was right?
//    recall    = tp / (tp + fn)   — of everything real, how much did we catch?
//    f1        = 2·precision·recall / (precision + recall)
//    (If a denominator is 0, use 0.)
function scores(c: { tp: number; fp: number; tn: number; fn: number }) {
  return { accuracy: 0, precision: 0, recall: 0, f1: 0 }
}

const truth = [1, 1, 1, 0, 0, 0, 0, 0, 1, 0]
const pred  = [1, 0, 1, 1, 0, 0, 0, 0, 1, 0]
console.log(confusion(truth, pred), scores(confusion(truth, pred)))
`,
  solution: `function confusion(truth: number[], pred: number[]): { tp: number; fp: number; tn: number; fn: number } {
  const c = { tp: 0, fp: 0, tn: 0, fn: 0 }
  for (let i = 0; i < truth.length; i++) {
    if (truth[i] === 1 && pred[i] === 1) c.tp++
    else if (truth[i] === 0 && pred[i] === 1) c.fp++
    else if (truth[i] === 0 && pred[i] === 0) c.tn++
    else c.fn++
  }
  return c
}

function scores(c: { tp: number; fp: number; tn: number; fn: number }) {
  const total = c.tp + c.fp + c.tn + c.fn
  const precision = c.tp + c.fp ? c.tp / (c.tp + c.fp) : 0
  const recall = c.tp + c.fn ? c.tp / (c.tp + c.fn) : 0
  const f1 = precision + recall ? (2 * precision * recall) / (precision + recall) : 0
  return { accuracy: (c.tp + c.tn) / total, precision, recall, f1 }
}

const truth = [1, 1, 1, 0, 0, 0, 0, 0, 1, 0]
const pred  = [1, 0, 1, 1, 0, 0, 0, 0, 1, 0]
console.log(confusion(truth, pred), scores(confusion(truth, pred)))
`,
  tests: `
test("counts the four outcomes", () => expect(confusion([1, 1, 1, 0, 0, 0, 0, 0, 1, 0], [1, 0, 1, 1, 0, 0, 0, 0, 1, 0]), "confusion").toEqual({ tp: 3, fp: 1, tn: 5, fn: 1 }))
test("precision 3/4, recall 3/4", () => {
  const __s = scores({ tp: 3, fp: 1, tn: 5, fn: 1 })
  expect([__s.precision, __s.recall], "[precision, recall]").toBeCloseTo([0.75, 0.75])
})
test("F1 punishes imbalance between them", () => expect(scores({ tp: 1, fp: 0, tn: 5, fn: 9 }).f1, "f1 (precision 1, recall 0.1)").toBeCloseTo(2 * 0.1 / 1.1))
test("the lazy fraud model has recall 0", () => expect(scores({ tp: 0, fp: 0, tn: 950, fn: 50 }).recall, "recall").toBe(0))
test("…and accuracy 95%", () => expect(scores({ tp: 0, fp: 0, tn: 950, fn: 50 }).accuracy, "accuracy").toBeCloseTo(0.95))
`,
}

export const rocAuc: ExerciseDef = {
  starter: `// auc(scores, labels): the probability that a random positive gets a HIGHER score than a random negative.
// Check every (positive, negative) pair: count 1 if the positive scores higher, ½ for a tie, 0 otherwise.
// Divide by the number of pairs. 1.0 = perfect ranking, 0.5 = coin flipping.
function auc(scores: number[], labels: number[]): number {
  return 0.5
}

console.log(auc([0.9, 0.8, 0.3, 0.1], [1, 1, 0, 0]))   // perfect: 1
console.log(auc([0.9, 0.2, 0.6, 0.1], [1, 1, 0, 0]))   // one pair wrong: 0.75
`,
  solution: `function auc(scores: number[], labels: number[]): number {
  let wins = 0
  let pairs = 0
  for (let i = 0; i < scores.length; i++) {
    if (labels[i] !== 1) continue
    for (let j = 0; j < scores.length; j++) {
      if (labels[j] !== 0) continue
      pairs++
      if (scores[i] > scores[j]) wins += 1
      else if (scores[i] === scores[j]) wins += 0.5
    }
  }
  return wins / pairs
}

console.log(auc([0.9, 0.8, 0.3, 0.1], [1, 1, 0, 0]))
console.log(auc([0.9, 0.2, 0.6, 0.1], [1, 1, 0, 0]))
`,
  tests: `
test("perfect ranking → 1", () => expect(auc([0.9, 0.8, 0.3, 0.1], [1, 1, 0, 0]), "auc").toBe(1))
test("one pair out of four wrong → 0.75", () => expect(auc([0.9, 0.2, 0.6, 0.1], [1, 1, 0, 0]), "auc").toBeCloseTo(0.75))
test("completely backwards → 0", () => expect(auc([0.1, 0.2, 0.8, 0.9], [1, 1, 0, 0]), "auc").toBe(0))
test("all scores tied → 0.5", () => expect(auc([0.5, 0.5, 0.5], [1, 0, 0]), "auc").toBeCloseTo(0.5))
`,
}

// ─── 6.7 Generalisation (added: validation & cross-validation) ───────────────

const POLY_FIT = `// Fits a polynomial of a given degree by least squares (solving the "normal equations").
function fitPoly(xs: number[], ys: number[], degree: number): (x: number) => number {
  const feats = (x: number) => range(degree + 1).map(k => (x / 3) ** k)
  const A = xs.map(feats)
  const d = degree + 1
  const M = range(d).map(i => [...range(d).map(j => A.reduce((s, r) => s + r[i] * r[j], 0) + (i === j ? 1e-8 : 0)), A.reduce((s, r, n) => s + r[i] * ys[n], 0)])
  for (let c = 0; c < d; c++) {
    let p = c
    for (let r = c + 1; r < d; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r
    ;[M[c], M[p]] = [M[p], M[c]]
    for (let r = 0; r < d; r++) {
      if (r === c) continue
      const f = M[r][c] / M[c][c]
      for (let k = c; k <= d; k++) M[r][k] -= f * M[c][k]
    }
  }
  const w = M.map((row, i) => row[d] / row[i])
  return x => feats(x).reduce((s, v, k) => s + v * w[k], 0)
}`

export const crossValidate: ExerciseDef = {
  starter: `${POLY_FIT}

random.seed(5)
const xs = range(40).map(() => random.uniform(-3, 3))
const ys = xs.map(x => Math.sin(1.3 * x) + 0.25 * x + random.normal(0, 0.3))

// 1. folds(n, k): split the indices 0 … n−1 into k groups, round-robin: index i goes to group i % k.
function folds(n: number, k: number): number[][] {
  return []
}

// 2. cvError(degree, k): for each fold, fit on the OTHER folds and measure the mean squared
//    error on this fold. Return the average over the k folds.
function cvError(degree: number, k = 5): number {
  return 0
}

const errors = range(10).map(d => cvError(d + 1))
console.log(errors.map((e, i) => \`degree \${i + 1}: \${round(e, 3)}\`).join("\\n"))
console.log("best degree:", vec.argmin(errors) + 1)
plot.line(errors, { title: "cross-validated error for degree 1, 2, …, 10", xLabel: "degree − 1" })
`,
  solution: `${POLY_FIT}

random.seed(5)
const xs = range(40).map(() => random.uniform(-3, 3))
const ys = xs.map(x => Math.sin(1.3 * x) + 0.25 * x + random.normal(0, 0.3))

function folds(n: number, k: number): number[][] {
  const out: number[][] = range(k).map(() => [])
  for (let i = 0; i < n; i++) out[i % k].push(i)
  return out
}

function cvError(degree: number, k = 5): number {
  const groups = folds(xs.length, k)
  let total = 0
  for (const val of groups) {
    const isVal = new Set(val)
    const trainIdx = range(xs.length).filter(i => !isVal.has(i))
    const f = fitPoly(trainIdx.map(i => xs[i]), trainIdx.map(i => ys[i]), degree)
    total += vec.mean(val.map(i => (f(xs[i]) - ys[i]) ** 2))
  }
  return total / k
}

const errors = range(10).map(d => cvError(d + 1))
console.log(errors.map((e, i) => \`degree \${i + 1}: \${round(e, 3)}\`).join("\\n"))
console.log("best degree:", vec.argmin(errors) + 1)
plot.line(errors, { title: "cross-validated error for degree 1, 2, …, 10", xLabel: "degree − 1" })
`,
  tests: `
test("folds(7, 3) deals round-robin", () => expect(folds(7, 3), "folds(7, 3)").toEqual([[0, 3, 6], [1, 4], [2, 5]]))
test("every index lands in exactly one fold", () => expect(folds(40, 5).flat().sort((a, b) => a - b), "all indices").toEqual(range(40)))
test("a straight line underfits: worse than degree 5", () => expect(cvError(1) > cvError(5), "cvError(1) > cvError(5)").toBe(true))
test("the best degree is a middle one (3 to 7)", () => expect(vec.argmin(range(10).map(d => cvError(d + 1))) + 1, "best degree").toBeBetween(3, 7))
`,
}

// ─── 6.8 k-nearest neighbours ─────────────────────────────────────────────────

export const knnBasics: ExerciseDef = {
  starter: `// 1. distance(a, b): straight-line (Euclidean) distance between two points with any number of features.
function distance(a: number[], b: number[]): number {
  return 0
}

// 2. knnPredict(trainX, trainY, x, k): find the k training points closest to x and return the
//    most common label among them. (Sort the training indices by distance, take k, count votes.)
function knnPredict(trainX: number[][], trainY: number[], x: number[], k: number): number {
  return trainY[0]
}

random.seed(2)
const d = data.moons(300, 0.15)
const trainX = d.X.slice(0, 200), trainY = d.y.slice(0, 200)
const testX = d.X.slice(200), testY = d.y.slice(200)
for (const k of [1, 5, 15, 51, 151]) {
  const acc = testX.filter((x, i) => knnPredict(trainX, trainY, x, k) === testY[i]).length / testX.length
  console.log("k =", k, " test accuracy:", acc)
}
plot.scatter(d.X, { labels: d.y, title: "two moons" })
`,
  solution: `function distance(a: number[], b: number[]): number {
  let s = 0
  for (let i = 0; i < a.length; i++) s += (a[i] - b[i]) ** 2
  return Math.sqrt(s)
}

function knnPredict(trainX: number[][], trainY: number[], x: number[], k: number): number {
  const nearest = range(trainX.length)
    .sort((i, j) => distance(trainX[i], x) - distance(trainX[j], x))
    .slice(0, k)
  const votes = new Map<number, number>()
  for (const i of nearest) votes.set(trainY[i], (votes.get(trainY[i]) ?? 0) + 1)
  let best = trainY[nearest[0]]
  for (const [label, count] of votes) if (count > (votes.get(best) ?? 0)) best = label
  return best
}

random.seed(2)
const d = data.moons(300, 0.15)
const trainX = d.X.slice(0, 200), trainY = d.y.slice(0, 200)
const testX = d.X.slice(200), testY = d.y.slice(200)
for (const k of [1, 5, 15, 51, 151]) {
  const acc = testX.filter((x, i) => knnPredict(trainX, trainY, x, k) === testY[i]).length / testX.length
  console.log("k =", k, " test accuracy:", acc)
}
plot.scatter(d.X, { labels: d.y, title: "two moons" })
`,
  tests: `
test("distance([0, 0], [3, 4]) is 5", () => expect(distance([0, 0], [3, 4]), "distance").toBeCloseTo(5))
test("works in 3-D", () => expect(distance([1, 2, 3], [1, 2, 5]), "distance").toBeCloseTo(2))
test("k = 1 copies the nearest point", () => expect(knnPredict([[0, 0], [5, 5]], [7, 9], [4, 4], 1), "knnPredict").toBe(9))
test("k = 3 takes a majority vote", () => expect(knnPredict([[0, 0], [0.5, 0], [0, 0.5], [3, 3]], [1, 2, 2, 1], [0.1, 0.1], 3), "knnPredict").toBe(2))
test("…even when the single nearest point disagrees", () => expect(knnPredict([[0, 0], [0.5, 0], [0, 0.5], [3, 3]], [1, 2, 2, 1], [0.1, 0.1], 1), "knnPredict(k = 1)").toBe(1))
test("good on the moons with k = 5", () => {
  random.seed(2)
  const __d = data.moons(300, 0.15)
  const __acc = __d.X.slice(200).filter((x, i) => knnPredict(__d.X.slice(0, 200), __d.y.slice(0, 200), x, 5) === __d.y[200 + i]).length / 100
  expect(__acc, "accuracy").toBeGreaterThan(0.9)
})
`,
}

export const knnDigits: ExerciseDef = {
  starter: `// No training at all — just remember the examples. How well does that read handwriting?
const { X, y } = data.digits(1000)
const trainX = X.slice(0, 800), trainY = y.slice(0, 800)
const testX = X.slice(800), testY = y.slice(800)

function distance2(a: number[], b: number[]): number {
  let s = 0
  for (let i = 0; i < a.length; i++) s += (a[i] - b[i]) ** 2
  return s   // squared distance: same ordering, no square root needed
}

// classify(x): the label of the single closest training image (1-nearest neighbour).
function classify(x: number[]): number {
  return 0
}

const testAccuracy = testX.filter((x, i) => classify(x) === testY[i]).length / testX.length
console.log("test accuracy:", testAccuracy)
`,
  solution: `const { X, y } = data.digits(1000)
const trainX = X.slice(0, 800), trainY = y.slice(0, 800)
const testX = X.slice(800), testY = y.slice(800)

function distance2(a: number[], b: number[]): number {
  let s = 0
  for (let i = 0; i < a.length; i++) s += (a[i] - b[i]) ** 2
  return s
}

function classify(x: number[]): number {
  let best = 0
  for (let i = 1; i < trainX.length; i++) if (distance2(trainX[i], x) < distance2(trainX[best], x)) best = i
  return trainY[best]
}

const testAccuracy = testX.filter((x, i) => classify(x) === testY[i]).length / testX.length
console.log("test accuracy:", testAccuracy)
`,
  tests: `
test("nearest neighbour reads over 90% of unseen digits", () => expect(testAccuracy, "testAccuracy").toBeGreaterThan(0.9))
`,
}

// ─── 6.9 Decision trees ───────────────────────────────────────────────────────

const TREE_TYPES = `type Leaf = { leaf: true; label: number }
type Split = { leaf: false; feature: number; threshold: number; left: Tree; right: Tree }
type Tree = Leaf | Split`

const GINI_SPLIT_SOLUTION = `function gini(labels: number[]): number {
  if (labels.length === 0) return 0
  const counts = new Map<number, number>()
  for (const l of labels) counts.set(l, (counts.get(l) ?? 0) + 1)
  let g = 1
  for (const c of counts.values()) g -= (c / labels.length) ** 2
  return g
}

function bestSplit(X: number[][], y: number[]): { feature: number; threshold: number; score: number } {
  let best = { feature: 0, threshold: 0, score: Infinity }
  for (let f = 0; f < X[0].length; f++) {
    const values = [...new Set(X.map(x => x[f]))].sort((a, b) => a - b)
    for (let i = 0; i < values.length - 1; i++) {
      const t = (values[i] + values[i + 1]) / 2
      const left = y.filter((_, j) => X[j][f] < t)
      const right = y.filter((_, j) => X[j][f] >= t)
      const score = (left.length * gini(left) + right.length * gini(right)) / y.length
      if (score < best.score) best = { feature: f, threshold: t, score }
    }
  }
  return best
}`

export const giniSplit: ExerciseDef = {
  starter: `// 1. gini(labels): how mixed a group is. 1 − Σ (fraction of each label)².
//    gini([1, 1, 1]) → 0 (pure)     gini([0, 1]) → 0.5 (as mixed as two labels get)
function gini(labels: number[]): number {
  return 0
}

// 2. bestSplit(X, y): try every feature f and every threshold t halfway between two neighbouring
//    distinct values. Split into "x[f] < t" and the rest. Score = the size-weighted average gini
//    of the two sides. Return the split with the LOWEST score.
function bestSplit(X: number[][], y: number[]): { feature: number; threshold: number; score: number } {
  return { feature: 0, threshold: 0, score: Infinity }
}

const X = [[1, 7], [2, 3], [3, 8], [6, 2], [7, 9], [8, 4]]
const y = [0, 0, 0, 1, 1, 1]
console.log(gini(y), bestSplit(X, y))   // the cut should be on feature 0, between 3 and 6
`,
  solution: `${GINI_SPLIT_SOLUTION}

const X = [[1, 7], [2, 3], [3, 8], [6, 2], [7, 9], [8, 4]]
const y = [0, 0, 0, 1, 1, 1]
console.log(gini(y), bestSplit(X, y))
`,
  tests: `
test("a pure group has gini 0", () => expect(gini([1, 1, 1]), "gini([1, 1, 1])").toBe(0))
test("a 50/50 group has gini 0.5", () => expect(gini([0, 1, 0, 1]), "gini([0, 1, 0, 1])").toBeCloseTo(0.5))
test("three equal labels: 2/3", () => expect(gini([0, 1, 2]), "gini([0, 1, 2])").toBeCloseTo(2 / 3))
test("finds the perfect cut on feature 0 at 4.5", () => {
  const __s = bestSplit([[1, 7], [2, 3], [3, 8], [6, 2], [7, 9], [8, 4]], [0, 0, 0, 1, 1, 1])
  expect([__s.feature, __s.threshold, __s.score], "[feature, threshold, score]").toBeCloseTo([0, 4.5, 0])
})
test("finds a cut on feature 1 when that's the one that works", () => expect(bestSplit([[5, 1], [1, 2], [4, 8], [2, 9]], [0, 0, 1, 1]).feature, "feature").toBe(1))
`,
}

export const buildTree: ExerciseDef = {
  starter: `${TREE_TYPES}

// (from the last challenge)
${GINI_SPLIT_SOLUTION}

function majority(y: number[]): number {
  const counts = new Map<number, number>()
  for (const l of y) counts.set(l, (counts.get(l) ?? 0) + 1)
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
}

// 1. build(X, y, depth): a recursive tree.
//    Make a leaf (with the majority label) if depth is 0, the group is pure (gini 0), or the best split
//    doesn't help (its score isn't lower than gini(y)). Otherwise split, and build each side with depth − 1.
function build(X: number[][], y: number[], depth: number): Tree {
  return { leaf: true, label: majority(y) }
}

// 2. predict(tree, x): walk down the tree to a leaf.
function predict(tree: Tree, x: number[]): number {
  return 0
}

random.seed(3)
const d = data.moons(300, 0.2)
const trainX = d.X.slice(0, 200), trainY = d.y.slice(0, 200)
const testX = d.X.slice(200), testY = d.y.slice(200)
for (const depth of [1, 2, 4, 8, 16]) {
  const tree = build(trainX, trainY, depth)
  const acc = (X: number[][], y: number[]) => X.filter((x, i) => predict(tree, x) === y[i]).length / X.length
  console.log("depth", depth, " train:", round(acc(trainX, trainY), 3), " test:", round(acc(testX, testY), 3))
}
`,
  solution: `${TREE_TYPES}

${GINI_SPLIT_SOLUTION}

function majority(y: number[]): number {
  const counts = new Map<number, number>()
  for (const l of y) counts.set(l, (counts.get(l) ?? 0) + 1)
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
}

function build(X: number[][], y: number[], depth: number): Tree {
  const g = gini(y)
  if (depth === 0 || g === 0) return { leaf: true, label: majority(y) }
  const s = bestSplit(X, y)
  if (s.score >= g) return { leaf: true, label: majority(y) }
  const L = range(X.length).filter(i => X[i][s.feature] < s.threshold)
  const R = range(X.length).filter(i => X[i][s.feature] >= s.threshold)
  return {
    leaf: false,
    feature: s.feature,
    threshold: s.threshold,
    left: build(L.map(i => X[i]), L.map(i => y[i]), depth - 1),
    right: build(R.map(i => X[i]), R.map(i => y[i]), depth - 1),
  }
}

function predict(tree: Tree, x: number[]): number {
  if (tree.leaf) return tree.label
  return predict(x[tree.feature] < tree.threshold ? tree.left : tree.right, x)
}

random.seed(3)
const d = data.moons(300, 0.2)
const trainX = d.X.slice(0, 200), trainY = d.y.slice(0, 200)
const testX = d.X.slice(200), testY = d.y.slice(200)
for (const depth of [1, 2, 4, 8, 16]) {
  const tree = build(trainX, trainY, depth)
  const acc = (X: number[][], y: number[]) => X.filter((x, i) => predict(tree, x) === y[i]).length / X.length
  console.log("depth", depth, " train:", round(acc(trainX, trainY), 3), " test:", round(acc(testX, testY), 3))
}
`,
  tests: `
test("depth 0 is a single leaf with the majority label", () => expect(build([[1], [2], [3]], [5, 5, 7], 0), "build(…, 0)").toEqual({ leaf: true, label: 5 }))
test("one split separates a clean dataset", () => {
  const __t = build([[1], [2], [8], [9]], [0, 0, 1, 1], 3)
  expect([predict(__t, [0]), predict(__t, [10]), predict(__t, [4.9]), predict(__t, [5.1])], "predictions").toEqual([0, 1, 0, 1])
})
test("a deep tree memorises the training set", () => {
  random.seed(3)
  const __d = data.moons(200, 0.2)
  const __t = build(__d.X, __d.y, 30)
  expect(__d.X.filter((x, i) => predict(__t, x) === __d.y[i]).length / 200, "training accuracy").toBe(1)
})
`,
}

// ─── 6.11 Project: house prices ───────────────────────────────────────────────

const HOUSE_SETUP = `random.seed(42)
const { X, y } = data.housing(300)
const trainSet = { X: X.slice(0, 200), y: y.slice(0, 200) }
const valSet = { X: X.slice(200, 250), y: y.slice(200, 250) }
const testSet = { X: X.slice(250), y: y.slice(250) }   // don't touch until the very end!`

const HOUSE_LINEAR = `function standardizer(X: number[][]) {
  const d = X[0].length
  const mean = range(d).map(j => vec.mean(X.map(x => x[j])))
  const std = range(d).map(j => Math.sqrt(vec.mean(X.map(x => (x[j] - mean[j]) ** 2))))
  return (x: number[]) => x.map((v, j) => (v - mean[j]) / std[j])
}

// Trains a linear model on any list of features computed from a house. Returns a predictor.
function fitLinear(features: (x: number[]) => number[]): (x: number[]) => number {
  const F = trainSet.X.map(features)
  const z = standardizer(F)
  const Z = F.map(z)
  let w = new Array(Z[0].length).fill(0)
  let b = 0
  for (let e = 0; e < 1500; e++) {
    const gw = new Array(w.length).fill(0)
    let gb = 0
    for (let i = 0; i < Z.length; i++) {
      const err = vec.dot(w, Z[i]) + b - trainSet.y[i]
      for (let j = 0; j < w.length; j++) gw[j] += (2 * err * Z[i][j]) / Z.length
      gb += (2 * err) / Z.length
    }
    w = w.map((v, j) => v - 0.1 * gw[j])
    b -= 0.1 * gb
  }
  return x => vec.dot(w, z(features(x))) + b
}`

const RMSE_SOLUTION = `function rmse(predict: (x: number[]) => number, set: { X: number[][]; y: number[] }): number {
  return Math.sqrt(vec.mean(set.X.map((x, i) => (predict(x) - set.y[i]) ** 2)))
}`

export const houseBaseline: ExerciseDef = {
  starter: `${HOUSE_SETUP}
console.log("features: size m², bedrooms, age, km to centre.  price: thousands")

// 1. rmse(predict, set): root mean squared error — the typical size of a mistake, in thousands.
function rmse(predict: (x: number[]) => number, set: { X: number[][]; y: number[] }): number {
  return 0
}

// 2. baseline: a "model" that ignores the house and always predicts the average training price.
const baseline = (x: number[]) => 0

console.log("baseline validation RMSE:", round(rmse(baseline, valSet), 1))
plot.scatter(trainSet.X.map((x, i) => [x[3], trainSet.y[i]]), { title: "price vs. distance to the centre", xLabel: "km", yLabel: "price" })
`,
  solution: `${HOUSE_SETUP}
console.log("features: size m², bedrooms, age, km to centre.  price: thousands")

${RMSE_SOLUTION}

const baseline = (x: number[]) => vec.mean(trainSet.y)

console.log("baseline validation RMSE:", round(rmse(baseline, valSet), 1))
plot.scatter(trainSet.X.map((x, i) => [x[3], trainSet.y[i]]), { title: "price vs. distance to the centre", xLabel: "km", yLabel: "price" })
`,
  tests: `
test("rmse of a perfect model is 0", () => expect(rmse(x => x[0], { X: [[1], [2]], y: [1, 2] }), "rmse").toBe(0))
test("rmse of errors 3 and 4 is √12.5", () => expect(rmse(() => 0, { X: [[0], [0]], y: [3, 4] }), "rmse").toBeCloseTo(Math.sqrt(12.5)))
test("the baseline predicts the training average", () => expect(baseline([100, 3, 10, 5]), "baseline(any house)").toBeCloseTo(vec.mean(trainSet.y)))
test("the baseline is bad — over 80 thousand off", () => expect(rmse(baseline, valSet), "baseline RMSE").toBeGreaterThan(80))
`,
}

export const houseLinear: ExerciseDef = {
  starter: `${HOUSE_SETUP}

${RMSE_SOLUTION}

${HOUSE_LINEAR}

// 1. A plain linear model on the four raw features:
const linear = fitLinear(x => x)

// 2. Feature engineering. The scatter plot showed price falling fast near the centre, then levelling off —
//    a curve, not a line. Add a fifth feature that bends the same way: Math.log(1 + km).
const engineered = fitLinear(x => x)   // ← return [...x, something] instead

console.log("linear validation RMSE:     ", round(rmse(linear, valSet), 2))
console.log("engineered validation RMSE: ", round(rmse(engineered, valSet), 2))
`,
  solution: `${HOUSE_SETUP}

${RMSE_SOLUTION}

${HOUSE_LINEAR}

const linear = fitLinear(x => x)
const engineered = fitLinear(x => [...x, Math.log(1 + x[3])])

console.log("linear validation RMSE:     ", round(rmse(linear, valSet), 2))
console.log("engineered validation RMSE: ", round(rmse(engineered, valSet), 2))
`,
  tests: `
test("a linear model is about ten times better than the baseline", () => expect(rmse(linear, valSet), "linear RMSE").toBeLessThan(20))
test("the engineered feature helps on validation data", () => expect(rmse(engineered, valSet), "engineered RMSE").toBeLessThan(rmse(linear, valSet) - 1))
`,
}

export const houseFinal: ExerciseDef = {
  starter: `${HOUSE_SETUP}

${RMSE_SOLUTION}

${HOUSE_LINEAR}

const engineered = fitLinear(x => [...x, Math.log(1 + x[3])])

// 1. knnRegressor(k): predict a house's price as the AVERAGE price of the k most similar training houses.
//    Standardise first (so km and m² count equally) — use standardizer(trainSet.X).
function knnRegressor(k: number): (x: number[]) => number {
  return () => 0
}

// 2. Pick the best model by VALIDATION error, from: the engineered linear model, and kNN with k = 1, 3, 5, 10, 20.
const candidates: { name: string; predict: (x: number[]) => number }[] = [
  { name: "linear + log(km)", predict: engineered },
  ...[1, 3, 5, 10, 20].map(k => ({ name: \`kNN, k = \${k}\`, predict: knnRegressor(k) })),
]
for (const c of candidates) console.log(c.name.padEnd(18), "validation RMSE:", round(rmse(c.predict, valSet), 2))
const best = candidates[0]   // ← the candidate with the lowest validation RMSE

// 3. Only now — once — look at the test set.
console.log("chosen:", best.name, "  TEST RMSE:", round(rmse(best.predict, testSet), 2))
`,
  solution: `${HOUSE_SETUP}

${RMSE_SOLUTION}

${HOUSE_LINEAR}

const engineered = fitLinear(x => [...x, Math.log(1 + x[3])])

function knnRegressor(k: number): (x: number[]) => number {
  const z = standardizer(trainSet.X)
  const Z = trainSet.X.map(z)
  return x => {
    const q = z(x)
    const nearest = range(Z.length)
      .map(i => ({ i, d: Z[i].reduce((s, v, j) => s + (v - q[j]) ** 2, 0) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, k)
    return vec.mean(nearest.map(n => trainSet.y[n.i]))
  }
}

const candidates: { name: string; predict: (x: number[]) => number }[] = [
  { name: "linear + log(km)", predict: engineered },
  ...[1, 3, 5, 10, 20].map(k => ({ name: \`kNN, k = \${k}\`, predict: knnRegressor(k) })),
]
for (const c of candidates) console.log(c.name.padEnd(18), "validation RMSE:", round(rmse(c.predict, valSet), 2))
const best = candidates.reduce((a, b) => (rmse(b.predict, valSet) < rmse(a.predict, valSet) ? b : a))

console.log("chosen:", best.name, "  TEST RMSE:", round(rmse(best.predict, testSet), 2))
`,
  tests: `
test("kNN with k = 1 copies the nearest house's price", () => {
  const __p = knnRegressor(1)
  expect(__p(trainSet.X[7]), "knnRegressor(1)(a training house)").toBe(trainSet.y[7])
})
test("kNN beats the baseline", () => expect(rmse(knnRegressor(10), valSet), "kNN RMSE").toBeLessThan(60))
test("you picked the candidate with the lowest validation error", () => {
  const __errs = candidates.map(c => rmse(c.predict, valSet))
  expect(rmse(best.predict, valSet), "chosen model's validation RMSE").toBeCloseTo(Math.min(...__errs))
})
test("the final model is within ~15 thousand on unseen houses", () => expect(rmse(best.predict, testSet), "test RMSE").toBeLessThan(15))
`,
}
